import { Injectable, inject, effect } from '@angular/core';
import { NavigationItem, NavigationGroup, NavigationLink, NavigationDropdown } from '../models/navigation-item.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { FeatureFlagService, FeatureFlag } from '../../feature-flags';
import { AuthService } from '../../auth/services/auth.service';
import { rolesVisus } from '../../auth/visus-roles';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly flags = inject(FeatureFlagService);
  private readonly authService = inject(AuthService);

  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);
  private readonly _sidenavItems: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);
  private readonly _bottomNavItems: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);
  private readonly _sidenavGroups: BehaviorSubject<NavigationGroup[]> =
    new BehaviorSubject<NavigationGroup[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  get sidenavItems$(): Observable<NavigationItem[]> {
    return this._sidenavItems.asObservable();
  }

  get bottomNavItems$(): Observable<NavigationItem[]> {
    return this._bottomNavItems.asObservable();
  }

  get sidenavGroups$(): Observable<NavigationGroup[]> {
    return this._sidenavGroups.asObservable();
  }

  constructor() {
    this.loadNavigation();
    effect(() => {
      // Re-avalia o menu assim que o perfil PROVID ou usuário autenticado for carregado/atualizado
      this.authService.statusAcessoProvid();
      this.authService.perfilProvid();
      this.authService.user();
      this.loadNavigation();
    });
  }

  loadNavigation(): void {
    const allGroups: NavigationGroup[] = [
      {
        title: 'OPERACIONAL',
        items: [
          {
            type: 'link',
            label: 'Início',
            route: '/inicio',
            icon: 'home',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'dropdown',
            label: 'Consultas',
            icon: 'search',
            children: [
              {
                type: 'link',
                label: 'Pessoa',
                route: '/consultas/pessoa',
                icon: 'group_search',
                featureFlag: 'consultaPessoa'
              },
              {
                type: 'link',
                label: 'Veículo',
                route: '/consultas/veiculo',
                icon: 'car_crash',
                featureFlag: 'consultaVeiculo'
              },
              {
                type: 'link',
                label: 'Bicicleta',
                route: '/consultas/bicicleta',
                icon: 'pedal_bike',
                featureFlag: 'consultaBicicleta'
              },
              {
                type: 'link',
                label: 'Celular',
                route: '/consultas/celular',
                icon: 'smartphone',
                featureFlag: 'consultaCelular'
              }
            ]
          },
          {
            type: 'link',
            label: 'Chamados',
            route: '/copom',
            icon: 'phone_in_talk',
            featureFlag: 'copom',
            badge: {
              value: '0',
              bgClass: 'bg-badge-danger',
              textClass: 'text-pmdf-surface',
              badgeKey: 'copom'
            }
          },
          {
            type: 'link',
            label: 'DF 360',
            route: '/sentinela',
            icon: 'video_camera_front',
            featureFlag: 'sentinela',
            badge: {
              value: '0',
              bgClass: 'bg-badge-warning',
              textClass: 'text-pmdf-surface',
              badgeKey: 'sentinela'
            }
          },
          {
            type: 'dropdown',
            label: 'TCIs',
            icon: 'assignment',
            featureFlag: 'tci',
            children: [
              {
                type: 'link',
                label: 'Lista de TCIs',
                route: '/tci',
                icon: 'list_alt'
              },
              {
                type: 'link',
                label: 'Dashboard Gestor',
                route: '/tci/dashboard',
                icon: 'bar_chart',
                featureFlag: 'tciDashboardGestor'
              }
            ]
          },
          {
            type: 'link',
            label: 'Ordem de Serviço',
            route: '/ordem-servico',
            icon: 'task',
            featureFlag: 'ordemServico'
          },
          {
            type: 'link',
            label: 'Posto eleitoral',
            route: '/eleicoes/posto',
            icon: 'how_to_vote',
            svgIcon: 'eleicoes',
            featureFlag: 'eleicoesPosto'
          },
          {
            type: 'dropdown',
            label: 'PROVID',
            icon: 'shield_person',
            featureFlag: 'provid',
            children: [
              { type: 'link', label: 'Área de Trabalho', route: '/provid/workspace', icon: 'space_dashboard', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'OPERADOR'] },
              { type: 'link', label: 'Agenda', route: '/provid/agenda', icon: 'calendar_month', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'OPERADOR'] },
              {
                type: 'dropdown',
                label: 'Dashboards',
                icon: 'dashboard',
                providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'],
                children: [
                  { type: 'link', label: 'Processos', route: '/provid/dashboards/processos', icon: 'folder_open', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Visitas', route: '/provid/dashboards/visitas', icon: 'directions_walk', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Perfil da Mulher Atendida', route: '/provid/centro-comando/perfil-mulher', icon: 'female', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'FONAR — Gestão de Risco com IA', route: '/provid/centro-comando/fonar', icon: 'shield', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Pessoas Atendidas & Reincidência', route: '/provid/centro-comando/pessoas', icon: 'groups', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Processos — Copilot IA', route: '/provid/centro-comando/processos', icon: 'psychology', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Processos — KPIs & AIOps', route: '/provid/centro-comando/processos-kpis', icon: 'analytics', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Visitas — Esforço Operacional', route: '/provid/centro-comando/visitas', icon: 'directions_walk', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Sala Lilás — Monitoramento', route: '/provid/centro-comando/sala-lilas', icon: 'favorite', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'RAPs — Despacho Tático', route: '/provid/centro-comando/raps', icon: 'local_police', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
                  { type: 'link', label: 'Alertas & Fila de Acompanhamento', route: '/provid/centro-comando/alertas-fila', icon: 'notifications_active', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] }
                ]
              },
              { type: 'link', label: 'Processos', route: '/provid/processos', icon: 'folder_open', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'OPERADOR', 'CONSULTA'] },
              { type: 'link', label: 'Homologação', route: '/provid/homologacao', icon: 'fact_check', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
              { type: 'link', label: 'Ações Preventivas', route: '/provid/acoes-preventivas', icon: 'campaign', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM'] },
              { type: 'link', label: 'Sala Lilás', route: '/provid/sala-lilas', icon: 'volunteer_activism', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'OPERADOR'] },
              { type: 'link', label: 'Busca Ativa', route: '/provid/busca-ativa', icon: 'search', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'OPERADOR'] },
              { type: 'link', label: 'Relatórios', route: '/provid/relatorios/mensal', icon: 'summarize', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'CONSULTA'] },
              { type: 'link', label: 'Efetivo', route: '/provid/efetivo', icon: 'groups', providRoles: ['GESTOR_GERAL', 'GESTOR_UPM', 'OPERADOR'] }
            ]
          },
          {
            type: 'link',
            label: 'Coordenação',
            route: '/coordenacao',
            icon: 'dashboard',
            featureFlag: 'coordenacaoOperacional'
          }
        ]
      },
      {
        title: 'ADMINISTRATIVO',
        items: [
          {
            type: 'dropdown',
            label: 'RAP',
            icon: 'assignment',
            featureFlag: 'rap',
            children: [
              {
                type: 'link',
                label: 'Lista de RAPs',
                route: '/rap',
                icon: 'list_alt'
              },
              {
                type: 'link',
                label: 'Pesquisa RAP',
                route: '/rap/pesquisa',
                icon: 'manage_search'
              },
              {
                type: 'link',
                label: 'Homologação',
                route: '/rap/validacao',
                icon: 'fact_check',
                roles: ['app.nexus.oficial-dia', 'app.nexus.comandante', 'app.nexus.inteligencia']
              },
              {
                type: 'link',
                label: 'Fiscalização',
                route: '/rap/fiscalizacao',
                icon: 'policy',
                featureFlag: 'rapFiscalizacaoHomologacao',
                roles: ['app.nexus.comandante', 'app.nexus.inteligencia', 'app.nexus.oficial-dia']
              },
              {
                type: 'link',
                label: 'Dashboard RAP',
                route: '/rap/dashboard',
                icon: 'bar_chart',
                featureFlag: 'rapDashboard'
              },
              {
                type: 'link',
                label: 'Dashboard Eleitoral',
                route: '/rap/dashboard/eleitoral',
                icon: 'how_to_vote',
                featureFlag: 'rapDashboardEleitoral'
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Viatura',
            icon: 'directions_car',
            children: [
              {
                type: 'link',
                label: 'Minha viatura',
                route: '/viatura/servico',
                icon: 'directions_car',
                featureFlag: 'viaturaServico',
                roles: rolesVisus(),
              },
              {
                type: 'link',
                label: 'Abastecimento',
                route: '/viatura/abastecimento',
                icon: 'local_gas_station',
                featureFlag: 'viaturaAbastecimento',
              },
              {
                type: 'link',
                label: 'Vistoria',
                route: '/viatura/vistoria',
                icon: 'fact_check',
                featureFlag: 'viaturaVistoria',
                roles: rolesVisus(),
              },
              {
                type: 'link',
                label: 'Receber viatura',
                route: '/viatura/vistoria/recebimento',
                icon: 'draw',
                featureFlag: 'viaturaVistoria',
                roles: rolesVisus(),
              },
            ]
          }
        ]
      },
      {
        title: 'SISTEMA',
        items: [
          {
            type: 'link',
            label: 'Novidades',
            route: '/novidades',
            icon: 'campaign',
            featureFlag: 'novidades'
          },
          {
            type: 'link',
            label: 'Configurações',
            route: '/config',
            icon: 'settings',
            featureFlag: 'configuracoes'
          }
        ]
      }
    ];

    const sidenavGroups = this.filterGroupsByFlags(allGroups);
    this._sidenavGroups.next(sidenavGroups);

    const flatItems = sidenavGroups.flatMap(group => group.items);
    this._sidenavItems.next(flatItems);

    const allBottomNavItems: NavigationItem[] = [
      {
        type: 'link',
        label: 'Início',
        route: '/inicio',
        icon: 'home',
        routerLinkActiveOptions: { exact: true }
      },
      {
        type: 'link',
        label: 'Pessoa',
        route: '/consultas/pessoa',
        icon: 'group_search',
        featureFlag: 'consultaPessoa'
      },
      {
        type: 'link',
        label: 'COPOM',
        route: '/copom',
        icon: 'phone_in_talk',
        featureFlag: 'copom',
        highlighted: true,
        badge: {
          value: '0',
          bgClass: 'bg-pmdf-danger',
          textClass: 'text-pmdf-surface',
          badgeKey: 'copom'
        }
      },
      {
        type: 'link',
        label: 'DF 360',
        route: '/sentinela',
        icon: 'video_camera_front',
        featureFlag: 'sentinela',
        badge: {
          value: '0',
          bgClass: 'bg-pmdf-warning',
          textClass: 'text-pmdf-surface',
          badgeKey: 'sentinela'
        }
      },
      {
        type: 'action',
        action: 'open-quick-access',
        label: 'Menu',
        icon: 'menu',
      }
    ];

    this._bottomNavItems.next(this.filterItemsByFlags(allBottomNavItems));
    this._items.next(this._sidenavItems.value);
  }

  private filterGroupsByFlags(groups: NavigationGroup[]): NavigationGroup[] {
    return groups
      .filter(g => !g.featureFlag || this.flags.isEnabled(g.featureFlag as FeatureFlag))
      .map(g => ({
        ...g,
        items: this.filterItemsByFlags(g.items),
      }))
      .filter(g => g.items.length > 0);
  }

  private filterItemsByFlags(items: NavigationItem[]): NavigationItem[] {
    return items
      .filter(item => {
        if (item.type === 'link') {
          return this.isLinkVisible(item);
        }
        if (item.type === 'dropdown') {
          if (item.featureFlag && !this.flags.isEnabled(item.featureFlag as FeatureFlag)) {
            return false;
          }
          if (item.roles?.length && !this.authService.hasAnyRole(item.roles)) {
            return false;
          }
          if (item.providRoles?.length) {
            const perfil = this.authService.perfilProvid();
            if (perfil !== 'GESTOR_GERAL' && !item.providRoles.includes(perfil as any)) {
              return false;
            }
          }
        }
        return true;
      })
      .map(item => {
        if (item.type === 'dropdown') {
          return {
            ...item,
            children: this.filterItemsByFlags(item.children as NavigationItem[]) as Array<NavigationLink | NavigationDropdown>,
          };
        }
        return item;
      })
      .filter(item => {
        if (item.type === 'dropdown') {
          return item.children.length > 0;
        }
        return true;
      });
  }

  /** Um link fica visível apenas quando a flag (se houver) estiver ativa, role atendida e perfil do PROVID autorizado. */
  private isLinkVisible(item: { featureFlag?: string; roles?: string[]; providRoles?: ('GESTOR_GERAL' | 'GESTOR_UPM' | 'OPERADOR' | 'CONSULTA')[] }): boolean {
    if (item.featureFlag && !this.flags.isEnabled(item.featureFlag as FeatureFlag)) {
      return false;
    }
    if (item.roles?.length && !this.authService.hasAnyRole(item.roles)) {
      return false;
    }
    if (item.providRoles?.length) {
      const perfil = this.authService.perfilProvid();
      if (perfil !== 'GESTOR_GERAL' && !item.providRoles.includes(perfil as any)) {
        return false;
      }
    }
    return true;
  }
}
